import { format } from "date-fns";

import { ConnectProto } from "@racoon/api";
import { Language, Namespace, TranslationKey, getDateFnsLocale, getTypedT } from "@racoon/i18n";
import { formatPhoneNumber, fromTimestamp, toDateLike } from "@racoon/lib";

import { enumName, enumNumber } from "./job-enums";

export interface ShareReportPhoto {
  url: string;
  /** When the photo was taken, as the modal's carousel shows it; null when unknown. */
  takenAt: string | null;
}

/** Everything the report card prints, already formatted in the requested language. */
export interface ShareReport {
  /** Only completed jobs — by the driver or forced by a dispatcher — have a report worth sending. */
  canShare: boolean;
  fileName: string;
  title: string;
  /** The pickup location address, when it says more than the title. */
  address: string | null;
  jobType: string | null;
  jobNumber: string;
  routeNumber: string | null;
  completedAt: string | null;
  plateNumber: string | null;
  /** Model and type, printed side by side; empty when unknown. */
  truck: string[];
  driver: string | null;
  provider: { name: string; contactName: string | null; contactPhone: string | null } | null;
  receiver: string | null;
  /** Type name and volume, printed side by side; empty when unknown. */
  container: string[];
  photos: { before: ShareReportPhoto | null; after: ShareReportPhoto | null };
  generatedAt: string;
}

export interface BuildShareReportOptions {
  /** The service provider's primary contact; the provider line falls back to the name alone. */
  contact?: ConnectProto.Contact | null;
  lang: `${Language}`;
  /** Generation time printed in the footer. */
  now?: Date;
}

const intlLocales: Record<`${Language}`, string> = {
  [Language.Ru]: "ru-RU",
  [Language.En]: "en-US",
  [Language.Kk]: "kk-KZ",
};

const presentParts = (parts: (string | null | undefined)[]) =>
  parts.filter((part): part is string => Boolean(part?.trim()));

const SHAREABLE_STATUSES: readonly number[] = [
  ConnectProto.JobStatus.COMPLETED,
  ConnectProto.JobStatus.FORCE_COMPLETED,
];

export const canShareReport = (job: ConnectProto.Job) => {
  const status = enumNumber(ConnectProto.JobStatusSchema, job.status);
  return status !== undefined && SHAREABLE_STATUSES.includes(status);
};

/**
 * Turns a job into the view-model of its shareable photo report card: picks the
 * first BEFORE/AFTER photo and resolves enum labels and fallbacks, so the card
 * itself only lays the strings out.
 */
export const buildShareReport = (
  job: ConnectProto.Job,
  { contact, lang, now = new Date() }: BuildShareReportOptions,
): ShareReport => {
  const t = getTypedT(lang, Namespace.PhotoReport);
  const tEnum = getTypedT(lang, Namespace.Enum);
  const dateLocale = getDateFnsLocale(lang);

  const enumLabel = (
    schema: { values: readonly { number: number; name: string }[] },
    value: number | string | undefined,
  ) => {
    const number = enumNumber(schema, value);
    // 0 is every proto enum's UNDEFINED: nothing worth printing
    const name = number ? enumName(schema, number) : undefined;
    return name ? tEnum(name as TranslationKey<Namespace.Enum>) : null;
  };

  const factStartAt = fromTimestamp(job.factStartAt);
  const factEndAt = fromTimestamp(job.factEndAt);

  // file_reports replaces the deprecated service_started/ended_report_url
  // fields, so it wins; the old fields only matter for jobs that predate it.
  const firstPhoto = (
    type: ConnectProto.FileUploadType,
    legacyUrl: string,
    legacyTakenAt: Date | null,
  ): ShareReportPhoto | null => {
    const upload = (job.fileReports ?? []).find(
      (file) => file.url && enumNumber(ConnectProto.FileUploadTypeSchema, file.type) === type,
    );
    const url = upload?.url || legacyUrl;
    if (!url) return null;
    const takenAt = upload ? toDateLike(upload.createdAt) : legacyTakenAt;
    return {
      url,
      takenAt: takenAt ? format(takenAt, "HH:mm dd.MM.yy", { locale: dateLocale }) : null,
    };
  };

  const providerName = job.serviceProvider?.name?.trim();
  // A primary contact is often named after its organization ("ТОО Тазалык
  // Contact"); printing that next to the organization's name only repeats it.
  const contactName = contact?.name?.trim();
  const contactNameAddsInfo =
    contactName &&
    !(providerName && contactName.toLowerCase().includes(providerName.toLowerCase()));

  const truck = job.driverOnShift?.truck;
  const driver = job.driverOnShift?.driver;
  const containerType = job.containerType;
  // Type names often carry the volume already ("1.1 m3"); printing it twice reads as a typo.
  const nameMentionsVolume = (containerType?.name.match(/\d+(?:[.,]\d+)?/g) ?? []).some(
    (number) => Number(number.replace(",", ".")) === containerType?.volume,
  );
  const volume =
    containerType?.volume && !nameMentionsVolume
      ? t("share.volume", {
          volume: containerType.volume.toLocaleString(intlLocales[lang], {
            maximumFractionDigits: 2,
          }),
        })
      : null;

  const title = job.pickupLocation?.name?.trim() || job.pickupLocation?.address?.trim() || "";
  const address = job.pickupLocation?.address?.trim();
  const fileDate = factEndAt ?? fromTimestamp(job.planEndAt) ?? now;

  return {
    canShare: canShareReport(job),
    fileName: `report_Z${String(job.id)}_${format(fileDate, "yyyy-MM-dd")}.png`,
    title,
    address: address && address !== title ? address : null,
    jobType: enumLabel(ConnectProto.JobTypeSchema, job.type),
    jobNumber: `Z${String(job.id)}`,
    routeNumber: job.routeId ? `M${String(job.routeId)}` : null,
    // same format as the photo report modal's header
    completedAt: factEndAt ? format(factEndAt, "dd MMM yyyy HH:mm", { locale: dateLocale }) : null,
    plateNumber: truck?.plateNumber?.trim() || null,
    truck: truck
      ? presentParts([truck.model, enumLabel(ConnectProto.TruckTypeSchema, truck.type)])
      : [],
    driver: [driver?.firstName, driver?.lastName].filter(Boolean).join(" ").trim() || null,
    provider: providerName
      ? {
          name: providerName,
          contactName: contactNameAddsInfo ? contactName : null,
          contactPhone: contact?.phone ? formatPhoneNumber(contact.phone) : null,
        }
      : null,
    receiver: job.serviceReceiver?.name?.trim() || null,
    container: presentParts([containerType?.name, volume]),
    photos: {
      before: firstPhoto(
        ConnectProto.FileUploadType.JOB_SERVICE_STARTED,
        job.serviceStartedReportUrl,
        factStartAt ?? factEndAt,
      ),
      after: firstPhoto(
        ConnectProto.FileUploadType.JOB_SERVICE_ENDED,
        job.serviceEndedReportUrl,
        factEndAt,
      ),
    },
    generatedAt: t("share.generatedAt", {
      date: format(now, "dd.MM.yyyy HH:mm", { locale: dateLocale }),
    }),
  };
};
